import { useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { MAX_DESCRIPTION_LENGTH } from "@/lib/constants"
import { encryptTransactionFields } from "@/lib/crypto"
import { enqueueTransaction } from "@/lib/offline-queue"
import { useCryptoStore } from "@/store/crypto"

/** The POST /transactions body the web app sends (see the API's createTxnRoute). */
export interface CreateTransactionBody {
  date: string
  amount: string
  type: "debit" | "credit"
  description: string
  owner: string
  categoryId?: string
  referenceNumber?: string
  notes?: string
  isTransfer?: boolean
  isIgnored?: boolean
}

export type CreateTransactionResult = { success: boolean; id: string } | { offline: true }

/**
 * Create one transaction: POST /transactions, or queue it offline (synced on
 * reconnect) when the browser is offline or the request fails while offline.
 * Shared by the Add transaction dialog and the desktop quick-add composer.
 *
 * Private mode: description / referenceNumber / notes are encrypted with the
 * in-memory DEK BEFORE the request and before the offline queue — plaintext
 * never leaves this function. Private mode without the key fails closed
 * (nothing is sent or queued).
 */
export function useCreateTransaction() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (plain: CreateTransactionBody): Promise<CreateTransactionResult> => {
      const { key, mode } = useCryptoStore.getState()
      if (mode === "private" && !key) throw new Error("Unlock Paisa to add a transaction.")
      // The API caps plaintext at 500 (and ciphertext at what 500 chars encrypt
      // to), so check the PLAINTEXT here: after encryption the server can no
      // longer tell a 500-char description from a 600-char one.
      if (plain.description.length > MAX_DESCRIPTION_LENGTH)
        throw new Error(`Keep the description to ${MAX_DESCRIPTION_LENGTH} characters or fewer.`)
      // A DEK only exists in private mode, so key presence decides (as in
      // decryptTransactionRows); mode is passed for the standard-mode no-op.
      const body = await encryptTransactionFields(plain, key, key ? "private" : mode)
      if (!navigator.onLine) {
        await enqueueTransaction(body as never)
        return { offline: true as const }
      }
      try {
        return await api.post<{ success: boolean; id: string }>("/transactions", body)
      } catch (err) {
        if (!navigator.onLine) {
          await enqueueTransaction(body as never)
          return { offline: true as const }
        }
        throw err
      }
    },
    onSuccess: (res) => {
      if ("offline" in res) return
      queryClient.invalidateQueries({ queryKey: ["transactions"] })
      queryClient.invalidateQueries({ queryKey: ["dashboard"] })
    },
  })
}
